import { Stack } from '@mui/material';
import React from 'react';
import { Control, FieldPath, UseFormSetValue } from 'react-hook-form';

import ControlledTextField from '../../../components/TextField/ControlledTextField';
import { maskNumbers, preventNonNumbers } from '../../../utils/helpers';
import { AccountTypeFormValues } from '../validation';

interface AccountFormProps {
  control: Control<AccountTypeFormValues>;
  setANumber: (accountNumber: string) => void;
  setRNumber: (routingNumber: string) => void;
  setRhfValue: UseFormSetValue<AccountTypeFormValues>;
  showNicknameField?: boolean;
  editing?: boolean;
}

const AccountForm = ({
  control,
  setANumber,
  setRNumber,
  setRhfValue,
  showNicknameField,
  editing,
}: AccountFormProps) => {
  const handleChange =
    (fieldName: string) =>
    (
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    ) =>
      preventNonNumbers(e.target.value, (val: string) => {
        if (fieldName === 'routingNumber') {
          setRNumber(val);
        } else if (fieldName === 'accountNumber') {
          setANumber(val);
        }
        onChange(e);
      });

  const handleBlur =
    (fieldName: string) =>
    (
      e: React.FocusEvent<HTMLInputElement>,
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => void,
    ) => {
      const { value } = e.target;
      const routingNumberField = fieldName === 'routingNumber';

      if (!value.includes(String.fromCharCode(8226))) {
        if (routingNumberField) {
          setRNumber(value);
        } else {
          setANumber(value);
        }
      }

      if (
        (routingNumberField && value.length === 9) ||
        (!routingNumberField && value.length >= 4)
      ) {
        maskNumbers(value, (val: string) => {
          setRhfValue(fieldName as FieldPath<AccountTypeFormValues>, val);
          onBlur(e);
        });
      } else {
        onBlur(e);
      }
    };

  const handleFocus =
    (fieldName: string) => (e: React.FocusEvent<HTMLInputElement>) => {
      setRhfValue(fieldName as FieldPath<AccountTypeFormValues>, '');
    };

  return (
    <Stack spacing={2} direction="column" sx={{
      pb: 3
    }}>
      <ControlledTextField
        name="name"
        control={control}
        className="csaa-qm-block"
        label="Name on account"
        autoComplete="name"
        showClearIfValue
        handleClear={() => setRhfValue('name', '')}
        InputProps={{ required: true }}
      />
      <ControlledTextField
        control={control}
        className="csaa-qm-block"
        disabled={editing}
        name="routingNumber"
        label="Routing number"
        inputProps={{
          maxLength: 9,
          pattern: `([0-9]|•)*`,
        }}
        handleBlur={handleBlur('routingNumber')}
        handleChange={handleChange('routingNumber')}
        InputProps={{
          onFocus: handleFocus('routingNumber'),
          required: true,
        }}
        showClearIfValue
        handleClear={() => setRhfValue('routingNumber', '')}
      />
      <ControlledTextField
        control={control}
        className="csaa-qm-block"
        disabled={editing}
        name="accountNumber"
        label="Account number"
        inputProps={{
          maxLength: 17,
          pattern: '([0-9]|•)*',
        }}
        handleBlur={handleBlur('accountNumber')}
        handleChange={handleChange('accountNumber')}
        InputProps={{
          onFocus: handleFocus('accountNumber'),
          required: true,
        }}
        showClearIfValue
        handleClear={() => setRhfValue('accountNumber', '')}
      />
      {showNicknameField && (
        <ControlledTextField
          name="nickname"
          label={
            <span>
              Nickname <i>Optional</i>
            </span>
          }
          control={control}
          className="csaa-qm-block"
          optional
          showClearIfValue
          handleClear={() => setRhfValue('name', '')}
        />
      )}
    </Stack>
  );
};

export default AccountForm;
